import { Widget } from 'graphql-common';
import { LocalWidget, GridLayoutItem } from '../hooks/useWidgetEditor';

/**
 * Detects if there are unsaved changes by comparing local widget state with server data.
 *
 * @param localWidgets - Array of local widgets in edit mode
 * @param serverWidgets - Array of widgets from the server/API
 * @param localGrid - Current grid layout configuration
 * @returns true if there are unsaved changes, false otherwise
 */
export default function hasUnsavedChanges(
  localWidgets: LocalWidget[],
  serverWidgets: Widget[],
  localGrid: GridLayoutItem[],
): boolean {
  // If local widgets haven't been initialized yet, no changes
  if (localWidgets.length === 0 && serverWidgets.length === 0) {
    return false;
  }

  // Check for different widget counts (excluding destroyed widgets)
  const activeLocalWidgets = localWidgets.filter((w) => !w.isDestroyed);
  if (activeLocalWidgets.length !== serverWidgets.length) {
    return true;
  }

  // Check for new widgets (have tempId but no id)
  const hasNewWidgets = localWidgets.some(
    (w) => w.tempId && !w.id && !w.isDestroyed,
  );
  if (hasNewWidgets) {
    return true;
  }

  // Check for deleted widgets (marked with isDestroyed, but only if they have an id from server)
  const hasDeletedWidgets = localWidgets.some((w) => w.isDestroyed && w.id);
  if (hasDeletedWidgets) {
    return true;
  }

  // Check for edited widgets (have formValues property)
  const hasEditedWidgets = localWidgets.some(
    (w) => w.formValues && !w.isDestroyed,
  );
  if (hasEditedWidgets) {
    return true;
  }

  // Check for position/size changes by comparing grid layout with original widget grid data
  const hasLayoutChanges = localWidgets.some((widget) => {
    if (widget.isDestroyed || !widget.id) return false;

    const gridItem = localGrid.find((item) => item.i === widget.id);
    const originalWidget = serverWidgets.find((w) => w.id === widget.id);

    if (!gridItem || !originalWidget?.grid) return false;

    // Compare grid properties (x, y, w, h)
    return (
      gridItem.x !== originalWidget.grid.x ||
      gridItem.y !== originalWidget.grid.y ||
      gridItem.w !== originalWidget.grid.w ||
      gridItem.h !== originalWidget.grid.h
    );
  });

  return hasLayoutChanges;
}
